<template>
  <div class="wrapper" @mouseover="hover = true" @mouseleave="hover = false">
    <div ref="media" class="media" :class="classes" :style="styles">
      <template v-if="showMedia">
        <Transition :name="transitionName">
          <video
            v-if="isVideo"
            :loop="!showVideoControls"
            :muted="videoShouldBeMuted"
            playsinline
            disableRemotePlayback
            preload="auto"
            :autoplay="!showVideoControls"
            :controls="showVideoControls"
            :poster="videoPoster"
            @loadedmetadata="onMediaLoaded"
            @loadeddata="onMediaLoaded"
            @canplay="onVideoCanPlay"
            @stalled="attemptVideoRecovery"
            @waiting="attemptVideoRecovery"
          >
            <source
              v-for="source in responsiveSources"
              :key="source.url"
              :src="source.url"
              :type="source.mimeType"
            />
          </video>
          <picture v-else v-show="eager || mediaLoaded">
            <source
              v-if="largeMedia"
              :srcset="largeMedia"
              media="(min-width: 821px)"
            />
            <source
              v-if="mediumMedia"
              :srcset="mediumMedia"
              media="(min-width: 429px)"
            />
            <source :srcset="smallMedia" media="(max-width: 428px)" />
            <img
              :src="
                images.largeMedia || images.mediumMedia || images.smallMedia
              "
              :alt="images.altText"
              :fetchpriority="eager ? 'high' : 'auto'"
              @load="onMediaLoaded"
            />
          </picture>
        </Transition>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "Media",
  inject: ["mq"],
  props: {
    images: {
      type: Object,
      default: () => ({}),
    },
    hoverImages: {
      type: Object,
      default: () => ({}),
    },
    sources: {
      type: Array,
      default: () => [],
    },
    cover: {
      type: Boolean,
      default: false,
    },
    previewImages: {
      type: Array,
      default: () => [],
    },
    coverPosition: {
      type: String,
      default: "center center",
    },
    noTransition: {
      type: Boolean,
      default: false,
    },
    eager: {
      type: Boolean,
      default: false,
    },
    showVideoControls: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: null,
      intersected: false,
      mediaLoaded: false,
      hover: false,
      isPrimingPreview: false,
    };
  },
  computed: {
    classes() {
      return {
        "media--cover": this.cover,
        "media--video": this.isVideo,
        "media--controlled-video": this.isVideo && this.showVideoControls,
      };
    },
    videoShouldBeMuted() {
      // Mute while priming so we can briefly play to paint a preview frame
      // without a user gesture; then unmute when controls are on.
      if (this.isPrimingPreview) return true;
      return !this.showVideoControls;
    },
    styles() {
      const styles = {
        "--coverPosition": `${this.coverPosition}`,
      };

      if (this.blurryMedia) {
        styles["--blurryMedia"] = `url(${this.blurryMedia})`;
      }

      return styles;
    },
    responsiveSources() {
      if (!this.sources?.length) return null;

      const preferredSources = this.sources.filter((source) => {
        if (!source.mq) return true;
        return this.mq.mdPlus ? source.mq === "large" : source.mq !== "large";
      });

      const fallbackSources = this.sources.filter((source) => {
        if (!source.mq) return false;
        return this.mq.mdPlus ? source.mq !== "large" : source.mq === "large";
      });

      const orderedSources = [...preferredSources, ...fallbackSources];
      const sourcePriority = (source) => {
        const type = source?.mimeType?.toLowerCase() || "";

        // Prefer MP4 first for quicker/clearer startup and broader compatibility.
        if (type.includes("mp4")) return 0;
        // WebM next.
        if (type.includes("webm")) return 1;
        // HLS / m3u8 streams afterwards.
        if (type.includes("mpegurl") || type.includes("m3u8")) return 2;
        return 3;
      };
      const prioritizedSources = [...orderedSources].sort(
        (a, b) => sourcePriority(a) - sourcePriority(b)
      );

      // Keep stable ordering while removing duplicates by URL.
      const uniqueSources = prioritizedSources.filter((source, index, list) => {
        if (!source?.url) return false;
        return list.findIndex((item) => item.url === source.url) === index;
      });

      if (!this.showVideoControls || this.hasVideoPoster) {
        return uniqueSources;
      }

      return uniqueSources.map((source) => {
        if (!source.url || source.url.includes("#t=")) {
          return source;
        }

        return {
          ...source,
          url: `${source.url}#t=1`,
        };
      });
    },
    responsivePreviewImages() {
      if (this.previewImages.length === 0) return null;
      const previewImages = this.previewImages?.filter((image) => {
        if (!image.mq) {
          return true;
        }
        if (image.mq === "large") {
          return this.mq.mdPlus;
        }
        return !this.mq.mdPlus;
      });
      return previewImages[0]?.url ?? null;
    },
    hasVideoPoster() {
      if (this.previewImages.length > 0) {
        return Boolean(this.responsivePreviewImages);
      }

      if (!this.showVideoControls) {
        return false;
      }

      const { smallMedia, mediumMedia, largeMedia } = this.images || {};

      if (this.mq.mdPlus) {
        return Boolean(largeMedia || mediumMedia || smallMedia);
      }

      return Boolean(smallMedia || mediumMedia || largeMedia);
    },
    videoPoster() {
      if (this.responsivePreviewImages) {
        return this.responsivePreviewImages;
      }

      if (!this.isVideo || !this.showVideoControls || !this.hasVideoPoster) {
        return null;
      }

      const { smallMedia, mediumMedia, largeMedia } = this.images || {};

      if (this.mq.mdPlus) {
        return largeMedia || mediumMedia || smallMedia || null;
      }

      return smallMedia || mediumMedia || largeMedia || null;
    },
    showMedia() {
      return this.eager || this.intersected;
    },
    breakpointSources() {
      if (!this.sources?.length) return [];

      return this.sources.filter((source) => {
        if (!source?.mq) return true;
        return this.mq.mdPlus ? source.mq === "large" : source.mq !== "large";
      });
    },
    hasBreakpointImage() {
      const { smallMedia, mediumMedia, largeMedia } = this.images || {};

      if (this.mq.mdPlus) {
        return Boolean(largeMedia || mediumMedia || smallMedia);
      }

      return Boolean(smallMedia || mediumMedia || largeMedia);
    },
    isVideo() {
      if (!this.sources?.length) return false;
      if (this.breakpointSources.length) return true;

      // A source tagged for the other breakpoint should not replace an image.
      return !this.hasBreakpointImage;
    },
    transitionName() {
      return this.noTransition ? "" : "fade";
    },
    largeMedia() {
      return this.generateSrcset(
        this.images.largeMedia,
        this.images.largeMedia2x
      );
    },
    mediumMedia() {
      return this.generateSrcset(
        this.images.mediumMedia,
        this.images.mediumMedia2x
      );
    },
    smallMedia() {
      return this.generateSrcset(
        this.images.smallMedia,
        this.images.smallMedia2x
      );
    },
    blurryMedia() {
      if (this.showVideoControls) {
        return (
          this.images?.blurry ||
          this.images?.smallMedia ||
          this.images?.mediumMedia ||
          this.images?.largeMedia
        );
      }

      return this.images?.blurry;
    },
  },
  watch: {
    intersected(value) {
      if (value) {
        this.$nextTick(this.syncImageLoadState);
      }
    },
  },
  mounted() {
    if (this.eager) {
      this.intersected = true;
      this.$nextTick(this.syncImageLoadState);
      return;
    }

    // Start fetching one viewport before the media scrolls into view. Loading
    // only once intersecting meant every image/video entered the viewport empty
    // (a blank frame on each flick), and the block then grew from 0px to its
    // image height while on screen, which is the mobile layout shift behind
    // CRO audit F07.
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: "100% 0px 100% 0px",
    });
    this.observer.observe(this.$refs.media);
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    generateSrcset(src, src2x) {
      return src2x ? `${src} 1x, ${src2x} 2x` : src;
    },
    onMediaLoaded(event) {
      this.mediaLoaded = true;
      this.prepareControlledVideo(event?.target);
    },
    // A cached image can finish before the load listener is attached, which
    // would otherwise leave the picture hidden behind its blurry placeholder.
    syncImageLoadState() {
      if (this.isVideo || this.mediaLoaded) return;

      const image = this.$refs.media?.querySelector("img");
      if (image?.complete && image.naturalWidth > 0) {
        this.mediaLoaded = true;
      }
    },
    onVideoCanPlay(event) {
      this.prepareControlledVideo(event?.target);
    },
    prepareControlledVideo(video) {
      if (!this.showVideoControls || !video || video.tagName !== "VIDEO") {
        return;
      }

      // Avoid interrupting an in-flight preview prime from repeated load events.
      if (video.dataset.previewPriming === "true") {
        return;
      }

      if (video.dataset.previewPrimed === "true") {
        this.pauseControlledVideo(video);
        return;
      }

      this.primeVideoPreviewFrame(video);
    },
    async primeVideoPreviewFrame(video) {
      if (!this.showVideoControls || this.hasVideoPoster || !video) {
        return;
      }

      if (
        video.dataset.previewPrimed === "true" ||
        video.dataset.previewPriming === "true"
      ) {
        return;
      }

      video.dataset.previewPriming = "true";
      this.isPrimingPreview = true;

      let targetTime = 1;
      if (Number.isFinite(video.duration) && video.duration > 0) {
        targetTime =
          video.duration > 1 ? 1 : Math.min(0.1, video.duration / 50);
      }

      try {
        await this.$nextTick();

        // Briefly play while muted so the browser decodes and paints a frame.
        const playPromise = video.play();
        if (playPromise) {
          await playPromise;
        }

        video.pause();

        if (Math.abs(video.currentTime - targetTime) > 0.05) {
          await new Promise((resolve) => {
            const onSeeked = () => {
              video.removeEventListener("seeked", onSeeked);
              resolve();
            };
            video.addEventListener("seeked", onSeeked);
            try {
              video.currentTime = targetTime;
            } catch (error) {
              resolve();
            }
          });
        }

        video.pause();
        video.dataset.previewPrimed = "true";
      } catch (error) {
        try {
          video.currentTime = targetTime;
          video.pause();
          video.dataset.previewPrimed = "true";
        } catch (seekError) {
          // Best-effort only — leave the native poster/blank frame.
        }
      } finally {
        video.dataset.previewPriming = "false";
        this.isPrimingPreview = false;
        this.pauseControlledVideo(video);
      }
    },
    pauseControlledVideo(video) {
      if (!this.showVideoControls || !video || video.tagName !== "VIDEO") {
        return;
      }

      video.pause();
      video.autoplay = false;
    },
    async attemptVideoRecovery(event) {
      const video = event?.target;
      if (!video || video.dataset.recovered === "true") return;

      if (this.showVideoControls) {
        this.pauseControlledVideo(video);
        return;
      }

      video.dataset.recovered = "true";

      try {
        video.load();
        await video.play();
      } catch (error) {
        // Best-effort recovery only; keep silent to avoid noisy logs.
      }
    },
    handleIntersection([entry]) {
      if (entry && entry.isIntersecting) {
        this.intersected = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.media {
  background-size: cover;
  background-position: center center;
  background-image: var(--blurryMedia);
  overflow: hidden;

  img,
  video {
    max-width: 100%;
    height: auto;
    display: block;
    background-size: cover;
    background-position: center center;
    margin: 0;
  }
}

.media--cover {
  height: 100%;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: var(--coverPosition);
  }
}

.media--video {
  &:not(.media--controlled-video) {
    background-image: none;
  }

  video {
    transform: translateZ(0);
    backface-visibility: hidden;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
